import { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import {
  getVenueLocation,
  getTrainingAttendance,
  submitTrainingAttendance,
} from '../../../../services/adminApiService/batchManagementApi/BatchManagementApiService';

function haversineMeters(lat1, lng1, lat2, lng2) {
  const R = 6371000;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) *
      Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function clampDate(date, min, max) {
  if (date < min) return min;
  if (max && date > max) return max;
  return date;
}

function todayString() {
  return new Date().toISOString().slice(0, 10);
}

const AttendanceModal = ({ show, onHide, training }) => {
  const [geoStatus, setGeoStatus] = useState('idle');
  const [coachPosition, setCoachPosition] = useState(null);
  const [venueInfo, setVenueInfo] = useState(null);
  const [distanceMeters, setDistanceMeters] = useState(null);
  const [sessionDate, setSessionDate] = useState('');
  const [attendance, setAttendance] = useState({});
  const [athletes, setAthletes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Reset state when modal closes
  useEffect(() => {
    if (!show) {
      setGeoStatus('idle');
      setCoachPosition(null);
      setVenueInfo(null);
      setDistanceMeters(null);
      setAttendance({});
      setAthletes([]);
    }
  }, [show]);

  // On open: compute default date, start geo + venue fetch
  useEffect(() => {
    if (!show || !training) return;

    const today = todayString();
    const minDate = training.training_date ?? today;
    const maxDate = training.training_end_date ?? minDate;
    const defaultDate = clampDate(today, minDate, maxDate);
    setSessionDate(defaultDate);

    setGeoStatus('loading');

    let resolvedPosition = null;
    let resolvedVenue = null;
    let geoResolved = false;
    let venueResolved = false;

    const tryFinalize = () => {
      if (!geoResolved || !venueResolved) return;

      const venue = resolvedVenue;
      if (!venue || !venue.latitude || !venue.longitude) {
        setGeoStatus('no_venue');
        return;
      }

      if (!resolvedPosition) return; // geo error path handled separately

      const dist = haversineMeters(
        resolvedPosition.latitude,
        resolvedPosition.longitude,
        parseFloat(venue.latitude),
        parseFloat(venue.longitude)
      );
      setDistanceMeters(dist);
      setGeoStatus(dist <= (venue.venue_radius ?? 500) ? 'in_range' : 'out_of_range');
    };

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolvedPosition = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        };
        setCoachPosition(resolvedPosition);
        geoResolved = true;
        tryFinalize();
      },
      () => {
        geoResolved = true;
        setGeoStatus('error');
      },
      { timeout: 10000 }
    );

    getVenueLocation(training.id)
      .then((res) => {
        const data = res?.data?.data ?? res?.data ?? null;
        resolvedVenue = data;
        setVenueInfo(data);
        venueResolved = true;
        tryFinalize();
      })
      .catch(() => {
        resolvedVenue = null;
        setVenueInfo(null);
        venueResolved = true;
        tryFinalize();
      });
  }, [show, training]);

  // Fetch athletes when geo allows and date changes
  useEffect(() => {
    if (!training || !sessionDate) return;
    if (geoStatus !== 'in_range' && geoStatus !== 'no_venue') return;

    setLoading(true);
    getTrainingAttendance(training.id, sessionDate)
      .then((res) => {
        const list = res?.data?.data ?? res?.data ?? [];
        setAthletes(list);
        const prefilled = {};
        list.forEach((a) => {
          if (a.status) prefilled[a.athlete_id] = a.status;
        });
        setAttendance(prefilled);
      })
      .catch(() => {
        setAthletes([]);
        setAttendance({});
      })
      .finally(() => setLoading(false));
  }, [geoStatus, sessionDate, training]);

  const toggleStatus = (athleteId, status) => {
    setAttendance((prev) => ({ ...prev, [athleteId]: status }));
  };

  const markAll = (status) => {
    const updated = {};
    athletes.forEach((a) => { updated[a.athlete_id] = status; });
    setAttendance(updated);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        session_date: sessionDate,
        coach_latitude: coachPosition?.latitude,
        coach_longitude: coachPosition?.longitude,
        attendance: athletes.map((a) => ({
          athlete_id: a.athlete_id,
          status: attendance[a.athlete_id] ?? 'absent',
        })),
      };
      await submitTrainingAttendance(training.id, payload);
      alert('Attendance saved successfully!');
      onHide();
    } catch (err) {
      alert(err?.response?.data?.message ?? 'Failed to save attendance');
    } finally {
      setSubmitting(false);
    }
  };

  const isLocked = geoStatus === 'out_of_range' || geoStatus === 'error';
  const canSubmit =
    !submitting &&
    !isLocked &&
    geoStatus !== 'loading' &&
    athletes.length > 0;

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Take Attendance — {training?.training_name}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Geo Status Banner */}
        {geoStatus === 'loading' && (
          <div className="alert alert-info">Checking your location...</div>
        )}
        {geoStatus === 'in_range' && (
          <div className="alert alert-success">
            Within venue range ({Math.round(distanceMeters)}m from venue)
          </div>
        )}
        {geoStatus === 'out_of_range' && (
          <div className="alert alert-danger">
            You are {Math.round(distanceMeters)}m from venue. Required: within{' '}
            {venueInfo?.venue_radius ?? 500}m. Attendance locked.
          </div>
        )}
        {geoStatus === 'error' && (
          <div className="alert alert-warning">
            Could not get your location. Attendance locked.
          </div>
        )}
        {geoStatus === 'no_venue' && (
          <div className="alert alert-info">
            Venue location not set. Attendance available without geo check.
          </div>
        )}

        {/* Date Selector */}
        <div className="mb-3">
          <label className="form-label">Session Date</label>
          <input
            type="date"
            className="form-control"
            value={sessionDate}
            min={training?.training_date}
            max={training?.training_end_date ?? training?.training_date}
            onChange={(e) => setSessionDate(e.target.value)}
            disabled={isLocked || geoStatus === 'loading'}
          />
        </div>

        {/* Mark All Buttons */}
        {(geoStatus === 'in_range' || geoStatus === 'no_venue') && athletes.length > 0 && (
          <div className="mb-3 d-flex gap-2">
            <button
              className="btn btn-sm btn-success"
              onClick={() => markAll('present')}
            >
              Mark All Present
            </button>
            <button
              className="btn btn-sm btn-danger"
              onClick={() => markAll('absent')}
            >
              Mark All Absent
            </button>
          </div>
        )}

        {/* Athletes Table */}
        {loading ? (
          <div>Loading athletes...</div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>#</th>
                <th>Athlete</th>
                <th>ID</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {athletes.length > 0 ? (
                athletes.map((athlete, idx) => (
                  <tr key={athlete.athlete_id}>
                    <td>{idx + 1}</td>
                    <td>{athlete.name}</td>
                    <td>{athlete.atom_id ?? '—'}</td>
                    <td>
                      <div className="btn-group">
                        <button
                          className={`btn btn-sm ${
                            attendance[athlete.athlete_id] === 'present'
                              ? 'btn-success'
                              : 'btn-outline-success'
                          }`}
                          onClick={() => toggleStatus(athlete.athlete_id, 'present')}
                          disabled={isLocked}
                        >
                          Present
                        </button>
                        <button
                          className={`btn btn-sm ${
                            attendance[athlete.athlete_id] === 'absent'
                              ? 'btn-danger'
                              : 'btn-outline-danger'
                          }`}
                          onClick={() => toggleStatus(athlete.athlete_id, 'absent')}
                          disabled={isLocked}
                        >
                          Absent
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="text-center">
                    No athletes mapped to this session
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </Modal.Body>

      <Modal.Footer>
        <button className="btn btn-secondary" onClick={onHide}>
          Close
        </button>
        <button
          className="btn btn-primary"
          onClick={handleSubmit}
          disabled={!canSubmit}
        >
          {submitting ? 'Saving...' : 'Save Attendance'}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default AttendanceModal;
